<template>
  <div class="traffic-boost">
    <section class="traffic-hero">
      <div class="traffic-hero__inner">
        <div class="traffic-hero__copy">
          <h1>流量速推</h1>
          <p class="traffic-hero__statement">让门店顾客成为内容传播节点</p>
          <p class="traffic-hero__summary">
            一枚长期稳定的门店二维码连接到店顾客与商家配置的视频、文案、话题和门店 POI；顾客扫码后进入内容页，在抖音客户端确认发布，后台再汇总扫码、发布准备与结果状态，形成可追溯的运营反馈。
          </p>
          <div class="traffic-hero__actions">
            <router-link class="traffic-button traffic-button--primary" to="/goin">
              咨询合作 <i class="el-icon-right" aria-hidden="true"></i>
            </router-link>
            <a class="traffic-button traffic-button--text" href="#traffic-flow">
              查看发布链路 <i class="el-icon-bottom" aria-hidden="true"></i>
            </a>
          </div>
        </div>

        <div
          class="traffic-preview"
          role="img"
          aria-label="流量速推产品界面示意，包括运营概览与顾客发布确认页面"
        >
          <div class="traffic-preview__canvas" aria-hidden="true">
            <div class="preview-dashboard">
              <div class="preview-dashboard__topbar">
                <div class="preview-dashboard__identity">
                  <span class="preview-dashboard__mark"></span>
                  <strong>流量速推 · 运营概览</strong>
                </div>
                <span>界面示意</span>
              </div>
              <div class="preview-dashboard__body">
                <div class="preview-dashboard__nav">
                  <span class="is-active"><i class="el-icon-data-analysis"></i>概览</span>
                  <span><i class="el-icon-office-building"></i>商家</span>
                  <span><i class="el-icon-video-camera"></i>素材</span>
                </div>
                <div class="preview-dashboard__content">
                  <div class="preview-dashboard__panels">
                    <div class="preview-panel preview-trend">
                      <div class="preview-panel__head">
                        <strong>扫码趋势</strong><span>示意走势</span>
                      </div>
                      <div class="preview-trend__chart">
                        <span></span><span></span><span></span><span></span><span></span><span></span>
                      </div>
                      <div class="preview-trend__legend"><i></i>扫码入口 <b></b>发布准备</div>
                    </div>
                    <div class="preview-panel preview-chain">
                      <div class="preview-panel__head"><strong>发布链路</strong><span>节点</span></div>
                      <div class="preview-chain__steps">
                        <span><i></i>扫码进入</span>
                        <span><i></i>内容准备</span>
                        <span><i></i>用户确认</span>
                      </div>
                    </div>
                  </div>
                  <div class="preview-dashboard__row">
                    <span class="preview-dashboard__row-icon"><i class="el-icon-office-building"></i></span>
                    <span><strong>示例门店</strong><small>永久二维码 · 长期入口</small></span>
                    <em>门店配置</em>
                  </div>
                  <div class="preview-dashboard__row">
                    <span class="preview-dashboard__row-icon"><i class="el-icon-video-camera"></i></span>
                    <span><strong>内容素材</strong><small>视频 · 文案 · 话题 · POI</small></span>
                    <em>持续更新</em>
                  </div>
                </div>
              </div>
            </div>

            <div class="preview-phone">
              <div class="preview-phone__speaker"></div>
              <div class="preview-phone__screen">
                <div class="preview-phone__status"><i></i><strong>发布确认</strong></div>
                <div class="preview-phone__store"><span>示例门店</span><small>内容传播页</small></div>
                <div class="preview-phone__qr">
                  <i class="qr-corner qr-corner--one"></i>
                  <i class="qr-corner qr-corner--two"></i>
                  <i class="qr-corner qr-corner--three"></i>
                </div>
                <div class="preview-phone__material"><i class="el-icon-video-camera"></i><span>内容素材</span></div>
                <div class="preview-phone__action"><i></i>打开抖音发布页</div>
                <p>发布前会停留在抖音确认页，由用户自行确认发布。</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="traffic-value" aria-labelledby="traffic-value-title">
      <div class="traffic-value__inner">
        <header class="traffic-section-head" v-reveal>
          <h2 id="traffic-value-title">把一次到店，连接成可持续运营的内容链路</h2>
          <p>入口保持稳定，内容持续更新，关键节点留下反馈，让门店能够围绕真实到店场景组织传播。</p>
        </header>
        <div class="traffic-value__items">
          <article>
            <span>01</span>
            <h3>到店场景</h3>
            <p>以门店永久二维码承接线下触点，让顾客在现场进入对应门店的内容页。</p>
          </article>
          <article>
            <span>02</span>
            <h3>内容资产</h3>
            <p>围绕门店维护视频、文案、话题与 POI，更新素材时无需重新印制二维码。</p>
          </article>
          <article>
            <span>03</span>
            <h3>数据反馈</h3>
            <p>汇总扫码、发布准备与结果状态，为门店复盘内容配置和运营链路提供依据。</p>
          </article>
        </div>
      </div>
    </section>

    <section id="traffic-flow" class="traffic-flow" aria-labelledby="traffic-flow-title">
      <div class="traffic-section-shell">
        <header class="traffic-section-head traffic-section-head--center" v-reveal>
          <h2 id="traffic-flow-title">从门店配置到传播反馈</h2>
          <p>五个连续阶段呈现产品的基础工作方式，页面不提供模拟操作。</p>
        </header>
        <ol class="traffic-flow__list" v-reveal="90">
          <li v-for="(step, index) in flowSteps" :key="step.title">
            <span class="traffic-flow__number">{{ String(index + 1).padStart(2, "0") }}</span>
            <div><h3>{{ step.title }}</h3><p>{{ step.detail }}</p></div>
          </li>
        </ol>
      </div>
    </section>

    <section class="traffic-capabilities" aria-labelledby="traffic-capability-title">
      <div class="traffic-section-shell">
        <header class="traffic-section-head traffic-section-head--split" v-reveal>
          <h2 id="traffic-capability-title">围绕门店内容传播的六项能力</h2>
          <p>从门店组织、内容维护到发布反馈，形成清晰且可持续配置的运营基础。</p>
        </header>
        <div class="traffic-capabilities__grid" v-reveal="90">
          <article v-for="capability in capabilities" :key="capability.title">
            <span class="traffic-capabilities__icon"><i :class="capability.icon" aria-hidden="true"></i></span>
            <div><h3>{{ capability.title }}</h3><p>{{ capability.detail }}</p></div>
          </article>
        </div>
      </div>
    </section>

    <section class="traffic-scenes" aria-labelledby="traffic-scenes-title">
      <div class="traffic-section-shell">
        <header class="traffic-section-head traffic-section-head--light" v-reveal>
          <h2 id="traffic-scenes-title">适配多种门店经营场景</h2>
          <p>能力按门店和内容组织，不以示例场景代替实际业务评估。</p>
        </header>
        <div class="traffic-scenes__grid" v-reveal="90">
          <article v-for="(scene, index) in scenes" :key="scene.name">
            <span>{{ String(index + 1).padStart(2, "0") }}</span>
            <h3>{{ scene.name }}</h3>
            <p>{{ scene.detail }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="traffic-boundary" aria-labelledby="traffic-boundary-title">
      <div class="traffic-boundary__inner" v-reveal>
        <header>
          <i class="el-icon-warning-outline" aria-hidden="true"></i>
          <h2 id="traffic-boundary-title">能力边界说明</h2>
        </header>
        <div>
          <p>本页仅用于介绍产品能力与业务链路，不构成开放平台权限、审核结果或可用范围的承诺。</p>
          <p class="traffic-boundary__notice">最终发布由用户在抖音客户端确认；真实能力取决于网站备案、应用状态、白名单配置与抖音开放平台官方权限审批。</p>
        </div>
      </div>
    </section>

    <section class="traffic-cta" aria-labelledby="traffic-cta-title">
      <div class="traffic-cta__inner" v-reveal>
        <div><h2 id="traffic-cta-title">让稳定入口连接门店的内容运营</h2><p>结合门店规模、内容基础与开放平台条件，评估适合的落地路径。</p></div>
        <router-link class="traffic-button traffic-button--primary" to="/goin">
          咨询合作 <i class="el-icon-right" aria-hidden="true"></i>
        </router-link>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  data() {
    return {
      flowSteps: [
        { title: "配置商家与素材", detail: "建立商家信息，配置视频、文案、话题与门店 POI。" },
        { title: "生成永久二维码", detail: "一个门店对应一个长期可用入口，后续更换素材无需重印。" },
        { title: "顾客扫码进入发布页", detail: "顾客到店扫码，查看本次可发布的视频与推荐文案。" },
        { title: "拉起抖音确认发布", detail: "系统准备发布内容，最终由顾客在抖音客户端确认。" },
        { title: "查看传播数据", detail: "后台汇总扫码、发布准备与结果状态，辅助复盘运营链路。" }
      ],
      capabilities: [
        { icon: "el-icon-office-building", title: "商家矩阵", detail: "集中维护单店与连锁门店，按商家组织内容和投放入口。" },
        { icon: "el-icon-full-screen", title: "永久二维码", detail: "二维码入口保持稳定，后台可持续更新关联素材和配置。" },
        { icon: "el-icon-video-camera", title: "视频素材库", detail: "按门店管理短视频素材，支持启用、停用与内容轮换。" },
        { icon: "el-icon-edit-outline", title: "文案、话题与 POI", detail: "统一配置推荐文案、话题标签和门店位置，减少顾客操作。" },
        { icon: "el-icon-circle-check", title: "发布结果跟踪", detail: "关联发布准备与回调结果，区分待确认、成功和失败状态。" },
        { icon: "el-icon-data-analysis", title: "流量趋势与转化漏斗", detail: "观察扫码到发布准备的趋势与环节变化，为运营调整提供依据。" }
      ],
      scenes: [
        { name: "餐饮探店", detail: "按门店配置菜品、环境与活动素材，承接顾客到店后的内容发布意愿。" },
        { name: "零售门店", detail: "围绕新品、陈列与门店体验组织可持续更新的内容入口。" },
        { name: "本地生活服务", detail: "适用于美业、健身等体验场景，按门店维护内容、话题和位置。" },
        { name: "连锁商家", detail: "统一维护门店矩阵，并为不同门店分别配置入口、素材与运营反馈。" }
      ]
    };
  }
};
</script>

<style lang="scss" scoped>
.traffic-boost,
.traffic-boost * {
  box-sizing: border-box;
}

.traffic-boost {
  min-width: 0;
  overflow: hidden;
  color: var(--ink-800);
  background: #fff;
}

.traffic-boost h1,
.traffic-boost h2,
.traffic-boost h3,
.traffic-boost p {
  margin: 0;
  letter-spacing: 0;
}

.traffic-boost h1,
.traffic-boost h2,
.traffic-boost h3 {
  color: var(--ink-950);
}

.traffic-boost p,
.traffic-boost h1,
.traffic-boost h2,
.traffic-boost h3,
.traffic-boost a,
.traffic-boost span {
  overflow-wrap: anywhere;
}

.traffic-section-shell,
.traffic-hero__inner,
.traffic-value__inner,
.traffic-boundary__inner,
.traffic-cta__inner {
  width: min(100%, 1240px);
  margin: 0 auto;
}

.traffic-section-shell {
  padding: var(--section-space) 24px;
}

.traffic-section-head {
  min-width: 0;
}

.traffic-section-head h2 {
  max-width: 720px;
  font-size: var(--text-h2);
  font-weight: 700;
  line-height: 1.32;
}

.traffic-section-head p {
  max-width: 680px;
  margin-top: 16px;
  color: var(--ink-600);
  font-size: 16px;
  line-height: 1.85;
}

.traffic-section-head--center {
  margin: 0 auto 54px;
  text-align: center;
}

.traffic-section-head--center h2,
.traffic-section-head--center p {
  margin-right: auto;
  margin-left: auto;
}

.traffic-section-head--split {
  display: grid;
  margin-bottom: 48px;
  align-items: end;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 0.8fr);
  gap: 48px;
}

.traffic-section-head--split p {
  margin-top: 0;
}

.traffic-button {
  display: inline-flex;
  min-height: 46px;
  padding: 0 22px;
  align-items: center;
  justify-content: center;
  gap: 9px;
  border-radius: var(--radius-sm);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
  text-decoration: none;
  transition: color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}

.traffic-button--primary {
  border: 1px solid var(--brand-700);
  color: #fff;
  background: var(--brand-700);
  box-shadow: 0 10px 24px rgba(18, 97, 142, 0.22);
}

.traffic-button--primary:hover {
  border-color: var(--brand-800);
  background: var(--brand-800);
  box-shadow: 0 14px 30px rgba(18, 97, 142, 0.28);
}

.traffic-button--text {
  padding-right: 8px;
  padding-left: 8px;
  color: var(--brand-700);
}

.traffic-button--text:hover {
  color: var(--brand-500);
}

.traffic-button:focus-visible {
  outline: 3px solid rgba(31, 145, 199, 0.4);
  outline-offset: 4px;
}

.traffic-hero {
  padding: 28px 24px;
  border-bottom: 1px solid var(--line);
  background: #fff;
}

.traffic-hero__inner {
  display: grid;
  min-height: 440px;
  align-items: center;
  grid-template-columns: minmax(0, 0.84fr) minmax(540px, 1.16fr);
  gap: 48px;
}

.traffic-hero__copy {
  min-width: 0;
  padding: 28px 0;
}

.traffic-hero h1 {
  font-size: var(--text-display);
  font-weight: 700;
  line-height: 1.08;
}

.traffic-hero__statement {
  margin-top: 18px !important;
  color: var(--brand-700);
  font-size: clamp(20px, 2.2vw, 26px);
  font-weight: 600;
  line-height: 1.45;
}

.traffic-hero__summary {
  max-width: 560px;
  margin-top: 22px !important;
  color: var(--ink-600);
  font-size: 16px;
  line-height: 1.9;
}

.traffic-hero__actions {
  display: flex;
  margin-top: 30px;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.traffic-preview {
  position: relative;
  width: 100%;
  min-width: 0;
  min-height: 390px;
  aspect-ratio: 16 / 10;
}

.traffic-preview__canvas {
  position: absolute;
  inset: 0;
}

.preview-dashboard {
  position: absolute;
  top: 7%;
  left: 0;
  width: 89%;
  height: 82%;
  overflow: hidden;
  border: 1px solid #dce5eb;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 28px 58px rgba(7, 43, 66, 0.16);
  animation: preview-enter 0.6s ease both;
}

.preview-dashboard__topbar {
  display: flex;
  height: 13%;
  min-height: 42px;
  padding: 0 18px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e5ebef;
  color: #8796a0;
  font-size: 10px;
}

.preview-dashboard__identity {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 9px;
}

.preview-dashboard__identity strong {
  overflow: hidden;
  color: #142e3e;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preview-dashboard__mark {
  width: 20px;
  height: 20px;
  flex: 0 0 auto;
  border: 5px solid var(--brand-600);
  border-right-color: var(--tone-traffic);
  border-radius: 50%;
}

.preview-dashboard__body {
  display: grid;
  height: 87%;
  grid-template-columns: 84px minmax(0, 1fr);
}

.preview-dashboard__nav {
  display: flex;
  padding: 16px 10px;
  flex-direction: column;
  gap: 7px;
  background: #0a314b;
}

.preview-dashboard__nav span {
  display: flex;
  min-height: 30px;
  padding: 0 8px;
  align-items: center;
  gap: 7px;
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 10px;
  white-space: nowrap;
}

.preview-dashboard__nav span.is-active {
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
}

.preview-dashboard__content {
  min-width: 0;
  padding: 14px;
  background: #f5f8fa;
}

.preview-dashboard__panels {
  display: grid;
  margin-bottom: 10px;
  grid-template-columns: minmax(0, 1.35fr) minmax(0, 0.85fr);
  gap: 10px;
}

.preview-panel {
  min-width: 0;
  padding: 12px;
  border: 1px solid #e1e8ed;
  border-radius: 6px;
  background: #fff;
}

.preview-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: #84939d;
  font-size: 8px;
}

.preview-panel__head strong {
  color: #1c3747;
  font-size: 10px;
}

.preview-trend__chart {
  display: flex;
  height: 65px;
  margin-top: 10px;
  padding: 0 4px 5px;
  align-items: flex-end;
  justify-content: space-between;
  gap: 7px;
  border-bottom: 1px solid #dfe8ec;
  background: repeating-linear-gradient(to bottom, transparent 0, transparent 20px, #edf2f4 21px);
}

.preview-trend__chart span {
  width: 12%;
  border-radius: 2px 2px 0 0;
  background: var(--tone-traffic);
}

.preview-trend__chart span:nth-child(1) { height: 30%; }
.preview-trend__chart span:nth-child(2) { height: 46%; }
.preview-trend__chart span:nth-child(3) { height: 38%; }
.preview-trend__chart span:nth-child(4) { height: 64%; }
.preview-trend__chart span:nth-child(5) { height: 54%; }
.preview-trend__chart span:nth-child(6) { height: 76%; }

.preview-trend__legend {
  display: flex;
  margin-top: 7px;
  align-items: center;
  gap: 5px;
  color: #87949c;
  font-size: 7px;
}

.preview-trend__legend i,
.preview-trend__legend b {
  width: 7px;
  height: 3px;
  border-radius: 1px;
  background: var(--tone-traffic);
}

.preview-trend__legend b {
  margin-left: 5px;
  background: var(--brand-500);
}

.preview-chain__steps {
  position: relative;
  display: flex;
  margin-top: 12px;
  flex-direction: column;
  gap: 9px;
}

.preview-chain__steps::before {
  content: "";
  position: absolute;
  top: 7px;
  bottom: 7px;
  left: 4px;
  width: 1px;
  background: #b6ded8;
}

.preview-chain__steps span {
  position: relative;
  display: flex;
  align-items: center;
  gap: 7px;
  color: #566c79;
  font-size: 8px;
}

.preview-chain__steps i {
  z-index: 1;
  width: 9px;
  height: 9px;
  border: 2px solid var(--tone-traffic);
  border-radius: 50%;
  background: #fff;
}

.preview-dashboard__row {
  display: grid;
  min-width: 0;
  min-height: 47px;
  padding: 7px 9px;
  align-items: center;
  grid-template-columns: 27px minmax(0, 1fr) auto;
  gap: 8px;
  border-top: 1px solid #e1e8ed;
  color: #214052;
  background: #fff;
}

.preview-dashboard__row:last-child {
  border-bottom: 1px solid #e1e8ed;
}

.preview-dashboard__row-icon {
  display: grid;
  width: 26px;
  height: 26px;
  place-items: center;
  border-radius: 5px;
  color: var(--brand-700);
  background: var(--brand-50);
}

.preview-dashboard__row strong,
.preview-dashboard__row small {
  display: block;
}

.preview-dashboard__row strong {
  font-size: 9px;
}

.preview-dashboard__row small {
  margin-top: 3px;
  color: #84949e;
  font-size: 7px;
}

.preview-dashboard__row em {
  color: var(--tone-traffic);
  font-size: 7px;
  font-style: normal;
}

.preview-phone {
  position: absolute;
  right: 0;
  bottom: 2%;
  width: 31%;
  height: 82%;
  min-width: 150px;
  padding: 8px;
  border: 1px solid #172b38;
  border-radius: 20px;
  background: #0c1f2a;
  box-shadow: 0 24px 48px rgba(5, 29, 44, 0.28);
  animation: phone-enter 0.7s 0.08s ease both;
}

.preview-phone__speaker {
  position: absolute;
  top: 7px;
  left: 50%;
  z-index: 2;
  width: 28%;
  height: 4px;
  border-radius: 4px;
  background: #203947;
  transform: translateX(-50%);
}

.preview-phone__screen {
  display: flex;
  height: 100%;
  padding: 16px 10px 10px;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  border-radius: 13px;
  background: #fff;
}

.preview-phone__status {
  display: flex;
  width: 100%;
  padding-bottom: 8px;
  align-items: center;
  gap: 6px;
  border-bottom: 1px solid #edf0f2;
  color: #1b3342;
  font-size: 9px;
}

.preview-phone__status i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #e83b4e;
}

.preview-phone__store {
  width: 100%;
  margin-top: 12px;
  text-align: center;
}

.preview-phone__store span,
.preview-phone__store small {
  display: block;
}

.preview-phone__store span {
  color: #183444;
  font-size: 11px;
  font-weight: 700;
}

.preview-phone__store small {
  margin-top: 3px;
  color: #87949c;
  font-size: 7px;
}

.preview-phone__qr {
  position: relative;
  width: 62px;
  height: 62px;
  margin: 11px 0 9px;
  border: 5px solid #fff;
  background-color: #102b3a;
  background-image: repeating-linear-gradient(0deg, transparent 0 5px, #fff 5px 8px), repeating-linear-gradient(90deg, transparent 0 5px, rgba(255, 255, 255, 0.78) 5px 8px);
  box-shadow: 0 0 0 1px #dbe3e8;
}

.qr-corner {
  position: absolute;
  width: 15px;
  height: 15px;
  border: 3px solid #102b3a;
  background: #fff;
}

.qr-corner::after {
  content: "";
  position: absolute;
  inset: 3px;
  background: #102b3a;
}

.qr-corner--one { top: 0; left: 0; }
.qr-corner--two { top: 0; right: 0; }
.qr-corner--three { bottom: 0; left: 0; }

.preview-phone__material {
  display: flex;
  width: 100%;
  min-height: 30px;
  padding: 0 8px;
  align-items: center;
  gap: 7px;
  border: 1px solid #e3e9ed;
  border-radius: 5px;
  color: #3c5665;
  font-size: 8px;
  background: #f7f9fa;
}

.preview-phone__material i {
  color: var(--brand-600);
}

.preview-phone__action {
  display: flex;
  width: 100%;
  min-height: 31px;
  margin-top: 8px;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid rgba(232, 59, 78, 0.32);
  border-radius: 5px;
  color: #cc3042;
  font-size: 8px;
  font-weight: 700;
  background: #fff4f5;
}

.preview-phone__action i {
  width: 0;
  height: 0;
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
  border-left: 6px solid #e83b4e;
}

.preview-phone__screen p {
  margin-top: 7px;
  color: #89969e;
  font-size: 6px;
  line-height: 1.5;
  text-align: center;
}

.traffic-value {
  padding: var(--section-space) 24px;
  background: #fff;
}

.traffic-value__inner {
  display: grid;
  align-items: start;
  grid-template-columns: minmax(260px, 0.78fr) minmax(0, 1.5fr);
  gap: 72px;
}

.traffic-value__items {
  display: grid;
  min-width: 0;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.traffic-value__items article {
  min-width: 0;
  padding: 6px 24px 4px;
  border-left: 1px solid var(--line);
}

.traffic-value__items article:first-child {
  padding-left: 0;
  border-left: 0;
}

.traffic-value__items span {
  color: var(--brand-600);
  font-size: 12px;
  font-weight: 800;
}

.traffic-value__items h3 {
  margin-top: 14px;
  font-size: 20px;
  line-height: 1.4;
}

.traffic-value__items p {
  margin-top: 12px;
  color: var(--ink-600);
  font-size: 14px;
  line-height: 1.8;
}

.traffic-flow {
  border-top: 1px solid #d8ece8;
  border-bottom: 1px solid #d8ece8;
  background: var(--tone-traffic-soft);
}

.traffic-flow__list {
  position: relative;
  display: grid;
  margin: 0;
  padding: 0;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 22px;
  list-style: none;
}

.traffic-flow__list::before {
  content: "";
  position: absolute;
  top: 22px;
  right: 8%;
  left: 8%;
  height: 2px;
  background: #9fd4cc;
}

.traffic-flow__list li {
  position: relative;
  z-index: 1;
  min-width: 0;
}

.traffic-flow__number {
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  border: 2px solid var(--tone-traffic);
  border-radius: 50%;
  color: var(--tone-traffic);
  background: var(--tone-traffic-soft);
  font-size: 12px;
  font-weight: 800;
}

.traffic-flow__list h3 {
  margin-top: 22px;
  font-size: 18px;
  line-height: 1.45;
}

.traffic-flow__list p {
  margin-top: 10px;
  color: #59736f;
  font-size: 14px;
  line-height: 1.75;
}

.traffic-capabilities {
  background: #fff;
}

.traffic-capabilities__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 52px;
}

.traffic-capabilities__grid article {
  display: grid;
  min-width: 0;
  padding: 25px 0;
  align-items: start;
  grid-template-columns: 48px minmax(0, 1fr);
  gap: 18px;
  border-top: 1px solid var(--line);
}

.traffic-capabilities__icon {
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  border-radius: 6px;
  color: var(--brand-700);
  background: var(--brand-50);
  font-size: 20px;
}

.traffic-capabilities__grid h3 {
  font-size: 19px;
  line-height: 1.45;
}

.traffic-capabilities__grid p {
  margin-top: 7px;
  color: var(--ink-600);
  font-size: 14px;
  line-height: 1.75;
}

.traffic-scenes {
  background: var(--brand-900);
}

.traffic-section-head--light h2 {
  color: #fff;
}

.traffic-section-head--light p {
  color: rgba(255, 255, 255, 0.66);
}

.traffic-scenes__grid {
  display: grid;
  margin-top: 54px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.traffic-scenes__grid article {
  min-width: 0;
  padding: 4px 28px 6px;
  border-left: 1px solid var(--glass-border);
}

.traffic-scenes__grid article:first-child {
  padding-left: 0;
  border-left: 0;
}

.traffic-scenes__grid span {
  color: var(--accent-500);
  font-size: 12px;
  font-weight: 800;
}

.traffic-scenes__grid h3 {
  margin-top: 18px;
  color: #fff;
  font-size: 21px;
  line-height: 1.4;
}

.traffic-scenes__grid p {
  margin-top: 12px;
  color: rgba(255, 255, 255, 0.66);
  font-size: 14px;
  line-height: 1.8;
}

.traffic-boundary {
  padding: clamp(56px, 6vw, 72px) 24px;
  border-bottom: 1px solid var(--line);
  background: #f6f8fa;
}

.traffic-boundary__inner {
  display: grid;
  grid-template-columns: minmax(230px, 0.56fr) minmax(0, 1.44fr);
  gap: 64px;
}

.traffic-boundary header {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 13px;
}

.traffic-boundary header i {
  color: var(--accent-500);
  font-size: 25px;
}

.traffic-boundary h2 {
  font-size: 24px;
  line-height: 1.4;
}

.traffic-boundary p {
  color: var(--ink-600);
  font-size: 15px;
  line-height: 1.85;
}

.traffic-boundary__notice {
  margin-top: 11px !important;
  color: var(--ink-800) !important;
  font-weight: 600;
}

.traffic-cta {
  padding: var(--section-space) 24px;
  background: #fff;
}

.traffic-cta__inner {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
}

.traffic-cta h2 {
  font-size: var(--text-h2);
  line-height: 1.35;
}

.traffic-cta p {
  margin-top: 12px;
  color: var(--ink-600);
  font-size: 15px;
  line-height: 1.8;
}

.traffic-cta .traffic-button {
  flex: 0 0 auto;
}

@keyframes preview-enter {
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes phone-enter {
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 768px) {
  .traffic-section-shell {
    padding: 64px var(--mobile-gutter);
  }

  .traffic-section-head h2 {
    font-size: 29px;
  }

  .traffic-section-head--center {
    margin-bottom: 38px;
    text-align: left;
  }

  .traffic-section-head--split,
  .traffic-hero__inner,
  .traffic-value__inner,
  .traffic-boundary__inner {
    grid-template-columns: 1fr;
  }

  .traffic-section-head--split {
    margin-bottom: 34px;
    gap: 14px;
  }

  .traffic-section-head--split p {
    margin-top: 0;
  }

  .traffic-hero {
    padding: 26px var(--mobile-gutter) 34px;
  }

  .traffic-hero__inner {
    min-height: 0;
    gap: 30px;
  }

  .traffic-hero__copy {
    padding: 10px 0 0;
  }

  .traffic-hero h1 {
    font-size: 44px;
  }

  .traffic-hero__statement {
    margin-top: 13px !important;
    font-size: 23px;
  }

  .traffic-hero__summary {
    margin-top: 16px !important;
    font-size: 14px;
    line-height: 1.8;
  }

  .traffic-hero__actions {
    margin-top: 22px;
  }

  .traffic-preview {
    width: 100%;
    max-width: 560px;
    min-height: 0;
    margin: 0 auto;
    aspect-ratio: 16 / 10;
  }

  .preview-dashboard {
    top: 5%;
    width: 91%;
    height: 76%;
  }

  .preview-dashboard__topbar {
    min-height: 34px;
    padding: 0 9px;
  }

  .preview-dashboard__topbar > span,
  .preview-dashboard__nav {
    display: none;
  }

  .preview-dashboard__body {
    grid-template-columns: 1fr;
  }

  .preview-dashboard__content {
    padding: 8px;
  }

  .preview-panel {
    padding: 8px;
  }

  .preview-trend__chart {
    height: 42px;
    margin-top: 6px;
  }

  .preview-chain__steps {
    margin-top: 7px;
    gap: 5px;
  }

  .preview-dashboard__row {
    min-height: 34px;
    padding: 4px 6px;
  }

  .preview-phone {
    width: 36%;
    min-width: 118px;
    height: 82%;
    padding: 6px;
  }

  .preview-phone__screen {
    padding: 13px 7px 7px;
  }

  .preview-phone__store {
    margin-top: 7px;
  }

  .preview-phone__qr {
    width: 45px;
    height: 45px;
    margin: 7px 0 6px;
  }

  .preview-phone__material,
  .preview-phone__action {
    min-height: 24px;
  }

  .traffic-value {
    padding: 64px var(--mobile-gutter);
  }

  .traffic-value__inner {
    gap: 36px;
  }

  .traffic-value__items,
  .traffic-flow__list,
  .traffic-capabilities__grid,
  .traffic-scenes__grid {
    grid-template-columns: 1fr;
  }

  .traffic-value__items article,
  .traffic-value__items article:first-child {
    padding: 24px 0;
    border-top: 1px solid var(--line);
    border-left: 0;
  }

  .traffic-flow__list {
    gap: 0;
  }

  .traffic-flow__list::before {
    top: 22px;
    right: auto;
    bottom: 22px;
    left: 21px;
    width: 2px;
    height: auto;
  }

  .traffic-flow__list li {
    display: grid;
    padding-bottom: 30px;
    grid-template-columns: 44px minmax(0, 1fr);
    gap: 18px;
  }

  .traffic-flow__list li:last-child {
    padding-bottom: 0;
  }

  .traffic-flow__list h3 {
    margin-top: 0;
  }

  .traffic-capabilities__grid article {
    padding: 22px 0;
  }

  .traffic-scenes__grid {
    margin-top: 36px;
  }

  .traffic-scenes__grid article,
  .traffic-scenes__grid article:first-child {
    padding: 24px 0;
    border-top: 1px solid var(--glass-border);
    border-left: 0;
  }

  .traffic-boundary {
    padding: 56px var(--mobile-gutter);
  }

  .traffic-boundary__inner {
    gap: 24px;
  }

  .traffic-cta {
    padding: 60px var(--mobile-gutter);
  }

  .traffic-cta__inner {
    align-items: flex-start;
    flex-direction: column;
    gap: 28px;
  }

  .traffic-cta h2 {
    font-size: 28px;
  }
}

@media (max-width: 420px) {
  .traffic-button {
    padding-right: 16px;
    padding-left: 16px;
  }

  .traffic-preview {
    min-height: 250px;
  }

  .preview-phone {
    height: 86%;
    min-width: 112px;
  }

  .preview-phone__screen {
    padding: 12px 6px 6px;
  }

  .preview-phone__status {
    padding-bottom: 5px;
  }

  .preview-phone__store {
    margin-top: 4px;
  }

  .preview-phone__qr {
    width: 38px;
    height: 38px;
    margin: 4px 0;
    border-width: 4px;
  }

  .qr-corner {
    width: 11px;
    height: 11px;
    border-width: 2px;
  }

  .qr-corner::after {
    inset: 2px;
  }

  .preview-phone__material,
  .preview-phone__action {
    min-height: 22px;
  }

  .preview-phone__action {
    margin-top: 5px;
  }

  .preview-phone__screen p {
    display: block;
    margin-top: 4px;
    font-size: 6px;
    line-height: 1.3;
  }
}

@media (prefers-reduced-motion: reduce) {
  .traffic-boost,
  .traffic-boost *,
  .traffic-boost *::before,
  .traffic-boost *::after {
    scroll-behavior: auto !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>
